import * as React from 'react';

import { enumUnit, type Unit } from '@rematter/frontend-lib-api-client';
import { Button, cn, getUnitSymbol, Row } from '@rematter/ui-web';
import { LucideCheckCircle, LucideLoader2 } from 'lucide-react';

import { useScaleWeight } from '~/modules/trade/hooks/useScaleWeight';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import type { ScaleSidecar } from '~/shared/graphql/queries/useScalesQuery';
import { getLocale } from '~/shared/utils/locale';

const formatWeight = (weight: number | null): string => {
  if (weight === null) return '0.000';
  return weight.toLocaleString(getLocale(), {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });
};

type ScaleWeightDisplayProps = {
  scaleId: string | undefined;
  sidecar: ScaleSidecar | null | undefined;
  unitOfMeasurement: string | undefined;
  hasLineItemFocused: boolean;
  onTakeWeight: (
    weight: number,
    isStable: boolean,
    unitOfMeasurement: Unit,
  ) => void;
};

export const ScaleWeightDisplay: React.FC<ScaleWeightDisplayProps> = ({
  scaleId,
  sidecar,
  unitOfMeasurement,
  hasLineItemFocused,
  onTakeWeight,
}) => {
  const t = useTranslations();

  const { weight: liveWeight } = useScaleWeight(scaleId, sidecar);
  const weight = liveWeight?.grossWeight ?? null;
  const isStable = liveWeight?.stable ?? false;
  const resolvedUnit = (liveWeight?.unitOfMeasurement ?? unitOfMeasurement) as
    | Unit
    | undefined;

  const handleTakeWeightPress = React.useCallback(() => {
    if (weight !== null) {
      onTakeWeight(weight, isStable, resolvedUnit ?? enumUnit.Pounds);
    }
  }, [onTakeWeight, isStable, weight, resolvedUnit]);

  return (
    <>
      <Row className="items-center justify-center gap-2 py-1 transition duration-500">
        <Row gap1 className="text-xl 2xl:text-2xl">
          <span className="text-neutral">{formatWeight(weight)}</span>
          <span className="font-extralight text-neutral/30">
            {getUnitSymbol(liveWeight?.unitOfMeasurement ?? unitOfMeasurement)}
          </span>
        </Row>

        <Row
          gap1
          className={cn(
            'items-center text-sm transition',
            isStable ? 'text-success' : 'text-neutral/30',
          )}
        >
          {isStable ? (
            <LucideCheckCircle size={20} />
          ) : (
            <LucideLoader2 size={20} className="animate-spin" />
          )}
        </Row>
      </Row>

      <Button
        variant="solid"
        className="py-2.5"
        onPress={handleTakeWeightPress}
        isDisabled={!isStable || !hasLineItemFocused}
        aria-label={t.loadStaged?.takeWeight}
      >
        {t.loadStaged?.takeWeight}{' '}
        <span className="ml-2 text-[0.625em] text-white/75 uppercase">
          {t.loadStaged?.space}
        </span>
      </Button>
    </>
  );
};
